import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';

const MESES = [
  { value: '01', label: 'Enero' },
  { value: '02', label: 'Febrero' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Abril' },
  { value: '05', label: 'Mayo' },
  { value: '06', label: 'Junio' },
  { value: '07', label: 'Julio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' }
];

const GastoFilters = ({
  filtroAnio,
  setFiltroAnio,
  filtroMes,
  setFiltroMes,
  filtroCategoria,
  setFiltroCategoria,
  filtroProveedor,
  setFiltroProveedor,
  filtroMedioPago,
  setFiltroMedioPago,
  mostrarPendientes,
  setMostrarPendientes,
  categorias,
  proveedores,
  mediosPago,
  aniosDisponibles,
  onLimpiarFiltros,
  totalGastos,
  gastosFiltrados
}) => {
  const hayFiltrosActivos = filtroMes || filtroCategoria || filtroProveedor || filtroMedioPago;

  return (
    <div className="card-beach mb-6">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2.5 sm:gap-3 flex-1 w-full">
          {/* Filtro por año */}
          <div className="col-span-1 sm:w-auto">
            <Select value={filtroAnio} onValueChange={setFiltroAnio}>
              <SelectTrigger className="w-full sm:w-[120px]" data-testid="filter-year">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                {aniosDisponibles.map(anio => (
                  <SelectItem key={anio} value={anio.toString()}>{anio}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro por mes */}
          <div className="col-span-1 sm:w-auto">
            <Select value={filtroMes} onValueChange={setFiltroMes}>
              <SelectTrigger className="w-full sm:w-[150px]" data-testid="filter-month">
                <SelectValue placeholder="Mes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los meses</SelectItem>
                {MESES.map(mes => (
                  <SelectItem key={mes.value} value={mes.value}>{mes.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro por categoría */}
          <div className="col-span-2 sm:col-span-1 sm:w-auto">
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-category">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las categorías</SelectItem>
                {[...categorias].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(cat => (
                  <SelectItem key={cat.id_categoria} value={cat.id_categoria}>{cat.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro por proveedor */}
          <div className="col-span-2 sm:col-span-1 sm:w-auto">
            <Select value={filtroProveedor} onValueChange={setFiltroProveedor}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-supplier">
                <SelectValue placeholder="Proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los proveedores</SelectItem>
                {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(prov => (
                  <SelectItem key={prov.id_proveedor} value={prov.id_proveedor}>{prov.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Filtro por medio de pago */}
          <div className="col-span-2 sm:col-span-1 sm:w-auto">
            <Select value={filtroMedioPago} onValueChange={setFiltroMedioPago}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-payment-method">
                <SelectValue placeholder="Medio de Pago" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los medios</SelectItem>
                {[...mediosPago].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(medio => (
                  <SelectItem key={medio.id_medio_pago} value={medio.id_medio_pago}>{medio.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Toggle pendientes */}
          <div className="col-span-2 sm:col-span-1 flex items-center justify-between sm:justify-start gap-2 px-3 py-2 bg-[#F7E8C9]/30 rounded-lg">
            <Checkbox
              id="mostrar-pendientes"
              checked={mostrarPendientes}
              onCheckedChange={setMostrarPendientes}
            />
            <label htmlFor="mostrar-pendientes" className="text-sm text-[#0E3A53] cursor-pointer">
              Mostrar pendientes
            </label>
          </div>

          {/* Limpiar filtros */}
          {hayFiltrosActivos && (
            <div className="col-span-2 sm:col-span-1">
              <Button 
                variant="outline" 
                onClick={onLimpiarFiltros}
                className="w-full sm:w-auto text-[#FF7F6A] border-[#FF7F6A] hover:bg-[#FF7F6A]/10"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4 mr-1" />
                Limpiar
              </Button>
            </div>
          )}
        </div>

        {/* Contador de resultados */}
        <div className="text-sm text-[#0E3A53]/60 w-full lg:w-auto text-right lg:text-left mt-1 lg:mt-0">
          Mostrando <span className="font-semibold text-[#FF7F6A]">{gastosFiltrados}</span> de{' '}
          <span className="font-semibold">{totalGastos}</span> gastos
        </div>
      </div>
    </div>
  );
};

export default GastoFilters;
